/**
 * Security & Trust Section
 * Compliance badges and security features
 */

import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import {
  Lock,
  Building2,
  Key,
  FileText,
  Shield,
  CheckCircle2,
  Server,
  Network,
} from 'lucide-react';
import { staggerContainerVariants, itemVariants } from '@/components/animations/variants';

interface SecurityFeature {
  icon: React.ReactNode;
  title: string;
  description: string;
}

interface ComplianceBadge {
  name: string;
  status: 'certified' | 'in-progress' | 'compliant';
  description: string;
}

const complianceBadges: ComplianceBadge[] = [
  {
    name: 'SOC 2 Type II',
    status: 'in-progress',
    description: 'Q2 2026',
  },
  {
    name: 'GDPR',
    status: 'compliant',
    description: 'Compliant by design',
  },
  {
    name: 'ISO 27001',
    status: 'in-progress',
    description: 'In progress',
  },
  {
    name: 'HIPAA',
    status: 'compliant',
    description: 'BAA available',
  },
];

const leftFeatures: SecurityFeature[] = [
  {
    icon: <Lock className="w-6 h-6 text-green-500" />,
    title: 'Zero Prompt Storage',
    description: 'Only SHA-256 hashes stored. Prompts never leave your system. GDPR compliant by design.',
  },
  {
    icon: <Building2 className="w-6 h-6 text-primary" />,
    title: 'Multi-Tenant Isolation',
    description: 'Row-level security (RLS). Tenant-scoped queries. No data bleeding between organizations.',
  },
  {
    icon: <Key className="w-6 h-6 text-accent" />,
    title: 'Encryption at Rest & Transit',
    description: 'AES-256 encryption. TLS 1.3 for all connections. Automatic key rotation every 90 days.',
  },
];

const rightFeatures: SecurityFeature[] = [
  {
    icon: <FileText className="w-6 h-6 text-blue-500" />,
    title: 'Complete Audit Logging',
    description: 'Full audit trail of all routing decisions. Exportable logs. Tamper-proof records.',
  },
  {
    icon: <Shield className="w-6 h-6 text-purple-500" />,
    title: 'Zero Trust Architecture',
    description: 'mTLS for service communication. JWT with tenant claims. IP allowlisting available.',
  },
  {
    icon: <CheckCircle2 className="w-6 h-6 text-green-500" />,
    title: 'Compliance Ready',
    description: 'SOC 2 Type II (Q2 2026). GDPR compliant. ISO 27001 certification in progress.',
  },
];

function ComplianceBadgeCard({ badge }: { badge: ComplianceBadge }) {
  const statusColors = {
    certified: 'bg-green-500/10 border-green-500/50 text-green-500',
    'in-progress': 'bg-yellow-500/10 border-yellow-500/50 text-yellow-500',
    compliant: 'bg-blue-500/10 border-blue-500/50 text-blue-500',
  };

  return (
    <div
      className={`border-2 rounded-xl p-6 text-center transition-all hover:shadow-lg ${statusColors[badge.status]}`}
    >
      <div className="flex justify-center mb-3">
        <Shield className="w-10 h-10" />
      </div>
      <h4 className="font-bold text-lg mb-1">{badge.name}</h4>
      <p className="text-xs opacity-80">{badge.description}</p>
    </div>
  );
}

function SecurityFeatureCard({ feature }: { feature: SecurityFeature }) {
  return (
    <div className="flex items-start space-x-4 p-4 rounded-lg hover:bg-muted/30 transition-colors">
      <div className="flex-shrink-0 mt-1">{feature.icon}</div>
      <div>
        <h4 className="font-semibold mb-1">{feature.title}</h4>
        <p className="text-sm text-muted-foreground leading-relaxed">{feature.description}</p>
      </div>
    </div>
  );
}

export function SecuritySection() {
  return (
    <section className="py-16 px-4 bg-muted/30">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            Enterprise-Grade Security
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Built with privacy, compliance, and security at the core
          </p>
        </motion.div>

        <motion.div
          variants={staggerContainerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          className="space-y-12"
        >
          {/* Compliance Badges */}
          <motion.div variants={itemVariants}>
            <h3 className="text-2xl font-bold text-center mb-6">Compliance & Certifications</h3>
            <div className="grid md:grid-cols-4 gap-6">
              {complianceBadges.map((badge) => (
                <ComplianceBadgeCard key={badge.name} badge={badge} />
              ))}
            </div>
          </motion.div>

          {/* Security Features */}
          <motion.div variants={itemVariants}>
            <div className="bg-card border border-border rounded-xl p-8">
              <div className="grid md:grid-cols-2 gap-8">
                {/* Left Column */}
                <div className="space-y-4">
                  {leftFeatures.map((feature, index) => (
                    <SecurityFeatureCard key={index} feature={feature} />
                  ))}
                </div>

                {/* Right Column */}
                <div className="space-y-4">
                  {rightFeatures.map((feature, index) => (
                    <SecurityFeatureCard key={index} feature={feature} />
                  ))}
                </div>
              </div>
            </div>
          </motion.div>

          {/* Additional Security Features */}
          <motion.div variants={itemVariants}>
            <div className="grid md:grid-cols-3 gap-6">
              {/* Infrastructure Security */}
              <div className="bg-card border border-border rounded-xl p-6 text-center">
                <div className="flex justify-center mb-4">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <Server className="w-6 h-6 text-primary" />
                  </div>
                </div>
                <h4 className="font-semibold mb-2">Infrastructure Security</h4>
                <p className="text-sm text-muted-foreground">
                  AWS-hosted with VPC isolation. DDoS protection. Regular penetration testing.
                </p>
              </div>

              {/* Network Security */}
              <div className="bg-card border border-border rounded-xl p-6 text-center">
                <div className="flex justify-center mb-4">
                  <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center">
                    <Network className="w-6 h-6 text-accent" />
                  </div>
                </div>
                <h4 className="font-semibold mb-2">Network Security</h4>
                <p className="text-sm text-muted-foreground">
                  Private network endpoints. IP allowlisting. WAF protection with rate limiting.
                </p>
              </div>

              {/* Data Protection */}
              <div className="bg-card border border-border rounded-xl p-6 text-center">
                <div className="flex justify-center mb-4">
                  <div className="w-12 h-12 rounded-full bg-green-500/10 flex items-center justify-center">
                    <Lock className="w-6 h-6 text-green-500" />
                  </div>
                </div>
                <h4 className="font-semibold mb-2">Data Protection</h4>
                <p className="text-sm text-muted-foreground">
                  No raw prompt storage. SHA-256 hashing. GDPR compliant data handling.
                </p>
              </div>
            </div>
          </motion.div>

          {/* CTA */}
          <motion.div variants={itemVariants} className="text-center">
            <p className="text-muted-foreground mb-6">
              Want to learn more about our security practices?
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button size="lg" asChild>
                <a href="https://docs.naivana.io/" target="_blank" rel="noopener noreferrer">
                  View Security Documentation
                </a>
              </Button>
              <Button size="lg" variant="outline">
                Request Security Whitepaper
              </Button>
            </div>
          </motion.div>

          {/* Bottom Note */}
          <motion.div variants={itemVariants} className="text-center">
            <p className="text-sm text-muted-foreground">
              Questions about security? Contact our security team at{' '}
              <a href="mailto:security@naivana.io" className="text-accent hover:underline">
                security@naivana.io
              </a>
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
